import dayjs from 'dayjs'
import type { NavigateFunction } from 'router-v6'
import type { RLoginResponse } from 'v2/types/api/auth'

interface TwoFaRedirectOptions {
  loginResponse: RLoginResponse
  navigate: NavigateFunction
  /** Source of the redirect — determines which password-skip flag to set */
  source: 'sso' | 'invite'
  nextUrl?: string
}

/**
 * Redirects the user to the appropriate 2FA screen based on the login response.
 * - If `redirect_two_fa_auth` is true → phone setup screen (`/two-factor-auth`)
 * - Otherwise → OTP verification screen (`/two-factor-verify`)
 *
 * Used by SSO redirect, invite activation, and any other passwordless login flow.
 */
export const redirectToTwoFa = ({
  loginResponse,
  navigate,
  source,
  nextUrl = '',
}: TwoFaRedirectOptions) => {
  const isSso = source === 'sso'
  const isInvite = source === 'invite'

  if (loginResponse.redirect_two_fa_auth) {
    navigate('/two-factor-auth', {
      state: {
        email: loginResponse.email,
        accountID: String(loginResponse.account_id),
        aomUUID: loginResponse.aom_uuid,
        isSignup: false,
        isSso,
        isInvite,
        originalLoginResponse: loginResponse,
        region: loginResponse.region,
        nextUrl,
        verificationPending: true,
      },
    })
  } else {
    navigate('/two-factor-verify', {
      state: {
        phoneNumber: loginResponse.phone_number,
        sessionUuid: loginResponse.session_uuid,
        aomUUID: loginResponse.aom_uuid,
        accountID: String(loginResponse.account_id),
        method: 'sms',
        email: loginResponse.email,
        password: '',
        isSignup: false,
        isSso,
        isInvite,
        nextUrl,
        isAuthenticatorDefault: loginResponse.authenticator_status?.is_default ?? false,
        isAuthenticatorEnabled: loginResponse.authenticator_status?.is_present ?? false,
        originalLoginResponse: loginResponse,
        region: loginResponse.region,
        otpSentTime: dayjs().toISOString(),
      },
    })
  }
}
